import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import * as Updates from 'expo-updates';
import { RefreshCw, X } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import { trackUpdateAction, UPDATE_ACTIONS } from '@/analytics/update';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { ACCOUNT_BANNER_CONTENT } from '@/constants/banners';
import { useUpdateBanner } from '@/hooks/useUpdateBanner';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import { getAppVersion } from '@/util/getBuildInformation';

type UpdateReadyBannerViewProps = {
  onRestart: () => void;
  onDismiss: () => void;
};

/**
 * Presentational banner. Always renders with the provided handlers.
 * Production callers should use UpdateReadyBanner, which gates on
 * Updates.isUpdatePending and manages per-session dismissal.
 */
export function UpdateReadyBannerView({ onRestart, onDismiss }: UpdateReadyBannerViewProps) {
  return (
    <View className="bg-info-0">
      <HStack className={twJoin('mx-auto w-full items-center gap-3 py-3', ACCOUNT_BANNER_CONTENT)}>
        <Icon as={RefreshCw} className="text-info-800" />

        <Text className="flex-1 font-semibold text-info-800">An update is ready</Text>

        <Button
          size="sm"
          onPress={onRestart}
          {...getAccessibilityProps({
            label: 'Restart to apply update',
            role: 'button',
            hint: 'Reloads the app to install the downloaded update',
          })}>
          <ButtonText>Restart</ButtonText>
        </Button>

        <Pressable
          onPress={onDismiss}
          hitSlop={ICON_BUTTON_HIT_SLOP}
          {...getAccessibilityProps({
            label: 'Dismiss update notification',
            role: 'button',
            hint: 'Hides the banner until the next time the app cold-starts',
          })}>
          <Icon as={X} size="md" className="text-info-800" />
        </Pressable>
      </HStack>
    </View>
  );
}

/**
 * Passive banner for pending non-required OTA updates. Renders when
 * Updates.isUpdatePending is true and the user has not dismissed this
 * session. Restart applies the bundle via Updates.reloadAsync; X hides
 * the banner until the next cold start. Required updates use
 * RequiredUpdateModal.
 */
export function UpdateReadyBanner() {
  const { isUpdatePending, isDismissed, isOtaPromptMode, dismiss } = useUpdateBanner();

  if (isWeb || !isUpdatePending || isDismissed || !isOtaPromptMode) {
    return null;
  }

  async function handleRestart() {
    trackUpdateAction({
      updateType: 'background',
      action: UPDATE_ACTIONS.RESTART_FROM_ACCOUNT,
      currentVersion: getAppVersion() ?? '',
      userInitiated: true,
    });

    try {
      await Updates.reloadAsync();
    } catch {
      // Reload failures are rare and self-correct on the next cold start.
    }
  }

  function handleDismiss() {
    trackUpdateAction({
      updateType: 'background',
      action: UPDATE_ACTIONS.BANNER_DISMISSED,
      currentVersion: getAppVersion() ?? '',
      userInitiated: true,
    });

    dismiss();
  }

  return <UpdateReadyBannerView onRestart={() => handleRestart()} onDismiss={handleDismiss} />;
}
